import { validate as isValidUUID } from 'uuid';
import { supabase } from '../supabase';
import { type MemberRole } from '../types/tenant';
import { getOrganization } from './organizationService';
import { analyticsCapture } from '../lib/analytics';

// =============================================================================
// TYPES
// =============================================================================

export interface ProjectData {
  // General info
  projectName?: string;
  code?: string;
  date?: string;

  // Your details
  my_name?: string;
  my_company?: string;
  my_email?: string;
  my_phone?: string;
  my_ICE?: string;
  my_RC?: string;
  my_Patente?: string;
  my_IF?: string;

  // Client
  client_name?: string;
  client_company?: string;
  client_email?: string;
  client_phone?: string;
  place_name?: string;

  // Location
  location?: {
    lat: number;
    lng: number;
  };

  // System
  systemType?: string;
  consumption?: {
    method: string | null;
    data: unknown;
  };

  // Metadata (set automatically)
  orgId?: string;
  createdBy?: string;
  lastModifiedBy?: string;
  createdAt?: string;
  updatedAt?: string;

  // Allow other fields
  [key: string]: unknown;
}

export interface Project extends ProjectData {
  id: string;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Recursively remove undefined values from an object.
 */
function removeUndefinedDeep<T>(value: T): T | undefined {
  if (Array.isArray(value)) {
    return value.map((v) => removeUndefinedDeep(v)).filter((v) => v !== undefined) as unknown as T;
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    Object.keys(value as object).forEach((k) => {
      const v = removeUndefinedDeep((value as Record<string, unknown>)[k]);
      if (v !== undefined) out[k] = v;
    });
    return out as T;
  }
  return value === undefined ? undefined : value;
}

// Helper to convert Supabase timestamp to ISO string
function toTimestamp(date: Date | string | null): { toDate: () => Date } | null {
  if (!date) return null;
  const d = typeof date === 'string' ? new Date(date) : date;
  return {
    toDate: () => d,
  };
}

// =============================================================================
// MONTHLY PROJECT COUNTING
// =============================================================================

/**
 * Get the number of projects created in the current calendar month.
 * Used for enforcing monthly project creation limits.
 * Note: Deleted projects still count towards the monthly limit to prevent abuse.
 */
export async function getMonthlyProjectCreationCount(orgId: string): Promise<number> {
  const now = new Date();
  const startOfMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));

  const { count, error } = await supabase
    .from('projects')
    .select('*', { count: 'exact', head: true })
    .eq('organization_id', orgId)
    .gte('created_at', startOfMonth.toISOString());

  if (error) {
    console.error('Failed to get monthly project count:', error);
    return 0;
  }
  return count || 0;
}

// =============================================================================
// ORGANIZATION-SCOPED PROJECT OPERATIONS
// =============================================================================

/**
 * Create or update a project within an organization.
 * This is the new multi-tenant version.
 */
export async function createOrUpdateProject(
  orgId: string,
  userId: string,
  data: ProjectData,
  projectId?: string,
  userRole?: MemberRole | null
): Promise<{ projectId: string }> {
  const now = new Date().toISOString();

  // Verify organization exists and user has permissions
  const org = await getOrganization(orgId);
  if (!org) {
    throw new Error('Organization not found');
  }

  if (userRole === 'viewer') {
    throw new Error('Viewers cannot create or edit projects');
  }

  // Validate projectId format if provided (must be a valid UUID)
  if (projectId && !isValidUUID(projectId)) {
    console.warn(
      `[projectService] Invalid projectId format "${projectId}", treating as new project`
    );
    projectId = undefined;
  }

  try {
    // Check if project exists (only if projectId is provided and valid)
    let existingProject = null;
    let existingData: ProjectData | null = null;
    if (projectId) {
      const { data: found } = await supabase
        .from('projects')
        .select('id, created_at, created_by, data')
        .eq('id', projectId)
        .eq('organization_id', orgId)
        .single();
      existingProject = found;
      existingData = found?.data as ProjectData | null;
    }

    const isNewProject = !existingProject;

    const payloadRaw: ProjectData = {
      ...data,
      orgId,
      lastModifiedBy: userId,
      updatedAt: now,
      ...(isNewProject
        ? {
            createdBy: userId,
            createdAt: now,
          }
        : {}),
    };

    const payload = removeUndefinedDeep(payloadRaw);

    if (isNewProject) {
      // Use an atomic RPC to prevent concurrent requests from bypassing monthly limits.
      const { data: insertedProjectId, error } = await supabase.rpc('check_and_create_project', {
        p_org_id: orgId,
        p_max_projects: org.subscription.features.maxProjects,
        p_data: payload,
        p_created_by: userId,
      });

      if (error) {
        if (error.message.includes('MONTHLY_PROJECT_LIMIT_EXCEEDED')) {
          throw new Error(
            'Organization has reached the maximum number of project creations for this month. Please upgrade or wait until next month.'
          );
        }
        throw new Error('Failed to create project: ' + error.message);
      }

      if (!insertedProjectId || typeof insertedProjectId !== 'string') {
        throw new Error('Failed to create project: missing project id');
      }

      analyticsCapture('project_created', {
        system_type: typeof data.systemType === 'string' ? data.systemType : undefined,
        has_location: Boolean(data.location),
        has_consumption: Boolean(data.consumption?.method),
      });

      return { projectId: insertedProjectId };
    } else {
      // UPDATE existing project - merge with existing data to preserve keys like design2D
      const mergedData = {
        ...(existingData ?? {}),
        ...payload,
      };

      const { error } = await supabase
        .from('projects')
        .update({
          data: mergedData,
          last_modified_by: userId,
        })
        .eq('id', projectId)
        .eq('organization_id', orgId);

      if (error) {
        throw new Error('Failed to update project: ' + error.message);
      }

      return { projectId: projectId! };
    }
  } catch (err) {
    console.error('[projectService] Failed to save project:', err);
    throw err;
  }
}

/**
 * Get a project by ID within an organization.
 */
export async function getProject(orgId: string, projectId: string): Promise<Project | null> {
  const { data: project, error } = await supabase
    .from('projects')
    .select('*')
    .eq('id', projectId)
    .eq('organization_id', orgId)
    .single();

  if (error || !project) {
    return null;
  }

  return {
    id: project.id,
    ...(project.data as ProjectData),
    orgId: project.organization_id,
    createdBy: project.created_by,
    lastModifiedBy: project.last_modified_by,
    createdAt: project.created_at,
    updatedAt: project.updated_at,
  };
}

/**
 * Get all projects for an organization.
 */
export async function getOrganizationProjects(
  orgId: string,
  options?: {
    orderByField?: string;
    orderDirection?: 'asc' | 'desc';
    limitCount?: number;
  }
): Promise<Project[]> {
  let query = supabase.from('projects').select('*').eq('organization_id', orgId);

  // Apply ordering
  if (options?.orderByField) {
    const fieldMap: Record<string, string> = {
      updatedAt: 'updated_at',
      createdAt: 'created_at',
    };
    const dbField = fieldMap[options.orderByField] || options.orderByField;
    query = query.order(dbField, {
      ascending: options.orderDirection === 'asc',
    });
  } else {
    query = query.order('updated_at', { ascending: false });
  }

  // Apply limit
  if (options?.limitCount) {
    query = query.limit(options.limitCount);
  }

  const { data: projects, error } = await query;

  if (error || !projects) return [];

  return projects.map((project) => ({
    id: project.id,
    ...(project.data as ProjectData),
    orgId: project.organization_id,
    createdBy: project.created_by,
    lastModifiedBy: project.last_modified_by,
    createdAt: project.created_at,
    updatedAt: project.updated_at,
  }));
}

/**
 * Get recent projects for an organization.
 */
export async function getRecentProjects(orgId: string, count: number = 10): Promise<Project[]> {
  return getOrganizationProjects(orgId, {
    orderByField: 'updatedAt',
    orderDirection: 'desc',
    limitCount: count,
  });
}

/**
 * Delete a project from an organization.
 */
export async function deleteProject(orgId: string, projectId: string): Promise<void> {
  // Note: Subcollections like meteoHourly would need to be handled separately
  // For now, we just delete the project. If you have subcollections,
  // you may want to add a separate table for them or handle them in a cleanup function.

  const { error } = await supabase
    .from('projects')
    .delete()
    .eq('id', projectId)
    .eq('organization_id', orgId);

  if (error) {
    throw new Error('Failed to delete project: ' + error.message);
  }
}

/**
 * Duplicate a project within the same organization.
 * Copies all project data and associated meteorological data.
 */
export async function duplicateProject(
  orgId: string,
  sourceProjectId: string,
  userId: string,
  newName?: string
): Promise<{ projectId: string; projectName: string }> {
  const sourceProject = await getProject(orgId, sourceProjectId);
  if (!sourceProject) {
    throw new Error('Source project not found');
  }

  const { id, createdAt, updatedAt, createdBy, lastModifiedBy, ...projectData } = sourceProject;

  const duplicatedName = newName || `${projectData.projectName || 'Project'} (Copy)`;

  const result = await createOrUpdateProject(orgId, userId, {
    ...projectData,
    projectName: duplicatedName,
    code: undefined,
  });

  const newProjectId = result.projectId;

  try {
    const { data: meteoRecords, error: meteoError } = await supabase
      .from('project_meteo')
      .select('*')
      .eq('project_id', sourceProjectId);

    if (!meteoError && meteoRecords && meteoRecords.length > 0) {
      const newMeteoRecords = meteoRecords.map((record) => ({
        project_id: newProjectId,
        year_month: record.year_month,
        records: record.records,
        source: record.source,
        total_records: record.total_records,
      }));

      const { error: insertError } = await supabase.from('project_meteo').insert(newMeteoRecords);

      if (insertError) {
        console.warn('[duplicateProject] Failed to copy meteo data:', insertError.message);
      }
    }
  } catch (err) {
    console.warn('[duplicateProject] Error copying meteo data:', err);
  }

  return { projectId: newProjectId, projectName: duplicatedName };
}

// =============================================================================
// PROJECT SEARCH & FILTERING
// =============================================================================

/**
 * Search projects by name within an organization.
 */
export async function searchProjects(orgId: string, searchTerm: string): Promise<Project[]> {
  // For full-text search on JSONB, we'll fetch all and filter
  // For production, consider using PostgreSQL full-text search or Algolia
  const allProjects = await getOrganizationProjects(orgId);

  const lowerSearch = searchTerm.toLowerCase();
  return allProjects.filter(
    (project) =>
      project.projectName?.toLowerCase().includes(lowerSearch) ||
      project.code?.toLowerCase().includes(lowerSearch) ||
      project.client_name?.toLowerCase().includes(lowerSearch) ||
      project.client_company?.toLowerCase().includes(lowerSearch)
  );
}

/**
 * Get projects created by a specific user within an organization.
 */
export async function getProjectsByCreator(orgId: string, userId: string): Promise<Project[]> {
  const { data: projects, error } = await supabase
    .from('projects')
    .select('*')
    .eq('organization_id', orgId)
    .eq('created_by', userId)
    .order('created_at', { ascending: false });

  if (error || !projects) return [];

  return projects.map((project) => ({
    id: project.id,
    ...(project.data as ProjectData),
    orgId: project.organization_id,
    createdBy: project.created_by,
    lastModifiedBy: project.last_modified_by,
    createdAt: project.created_at,
    updatedAt: project.updated_at,
  }));
}
